import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { LevelId } from '../content/sequence';
import { recordResult, type BeatResult, type MasteryMap } from '../engine/mastery';
import { buildSession, nextBand } from '../engine/session';
import { cheapestUnowned, itemById } from '../content/hq';
import { wpm } from '../engine/fluency';

// Local-only save. Holds the spaced-repetition mastery map + session/band state that the
// practice engine drives (Phase 3), the Phase 4 reward economy (coins + the base), and the
// Phase 5 fluency signal (per-beat best Speed-Run times + a rolling speed proxy).

interface Settings {
  hearItHints: boolean;
}

/** Phase 5 fluency: fastest tap-through (ms) per beat, and a rolling WCPM proxy. */
interface FluencyState {
  bestMsByBeat: Record<string, number>;
  recentWpm: number[];
}
const emptyFluency = (): FluencyState => ({ bestMsByBeat: {}, recentWpm: [] });

/** Phase 7 story spine: which chapters' intro/victory cards he has already watched
 *  (a chapter id is a LevelId — see chapters.ts), so each plays once at the right beat. */
interface StorySeen {
  intros: LevelId[];
  victories: LevelId[];
}
const emptyStory = (): StorySeen => ({ intros: [], victories: [] });

interface GameState {
  coins: number;
  /** Per-pattern spaced-repetition state. */
  mastery: MasteryMap;
  /** Session counter (drives expanding intervals). */
  session: number;
  /** Current active level band. */
  band: LevelId;
  placementDone: boolean;
  /** Beat ids for the current session, and how far through it. */
  currentBeatIds: string[];
  beatIndex: number;
  /** HQ items he has built (Phase 4 reward economy). */
  owned: string[];
  /** The build he's currently saving toward (defaults to the cheapest unbuilt). */
  featuredGoal: string | null;
  /** His name, for by-name celebration (optional; text only for now). */
  kidName: string | null;
  /** Phase 5 fluency: per-beat best Speed-Run time + rolling speed proxy. */
  fluency: FluencyState;
  /** Phase 7 story spine: chapters whose intro/victory cards have been watched. */
  storySeen: StorySeen;
  settings: Settings;

  addCoins: (n: number) => void;
  recordBeat: (patternId: string, r: BeatResult) => void;
  startSession: () => void;
  nextBeat: () => void;
  completePlacement: (band: LevelId) => void;
  /** Build an HQ item: only if affordable AND not already owned. Coins minted by reading. */
  purchase: (itemId: string) => void;
  /** Re-aim the featured goal at a specific (unbuilt) item. */
  setGoal: (itemId: string) => void;
  /** Record a Speed-Run attempt: keep the fastest per beat + push the speed proxy. */
  recordSpeedRun: (beatId: string, timeMs: number, wordCount: number) => void;
  /** Mark a chapter's intro / victory story card as watched (idempotent). */
  markIntroSeen: (level: LevelId) => void;
  markVictorySeen: (level: LevelId) => void;
  resetProgress: () => void;
}

const DEFAULT_BAND: LevelId = 'digraphs';

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      coins: 0,
      mastery: {},
      session: 0,
      band: DEFAULT_BAND,
      placementDone: false,
      currentBeatIds: [],
      beatIndex: 0,
      owned: [],
      featuredGoal: null,
      kidName: null,
      fluency: emptyFluency(),
      storySeen: emptyStory(),
      settings: { hearItHints: true },

      addCoins: (n) => set((s) => ({ coins: s.coins + n })),
      recordBeat: (patternId, r) =>
        set((s) => ({ mastery: recordResult(s.mastery, patternId, s.session, r) })),
      startSession: () => {
        const s = get();
        const band = nextBand(s.mastery, s.band); // climb a level once the band is mastered
        const session = s.session + 1;
        const beats = buildSession(s.mastery, band, session);
        set({ band, session, currentBeatIds: beats.map((b) => b.id), beatIndex: 0 });
      },
      nextBeat: () => set((s) => ({ beatIndex: s.beatIndex + 1 })),
      completePlacement: (band) => set({ band, placementDone: true, currentBeatIds: [], beatIndex: 0 }),
      purchase: (itemId) => {
        const s = get();
        const item = itemById(itemId);
        // Guardrails: real item, affordable, not already built. Coins are only ever
        // minted by decoding, so spending can never buy a way past reading.
        if (!item || s.owned.includes(itemId) || s.coins < item.cost) return;
        const owned = [...s.owned, itemId];
        // Auto-advance the goal to the next cheapest unbuilt item (unless he'd re-aimed
        // elsewhere — but a just-built goal must move on regardless).
        const nextGoal =
          s.featuredGoal && s.featuredGoal !== itemId && !owned.includes(s.featuredGoal)
            ? s.featuredGoal
            : (cheapestUnowned(owned)?.id ?? null);
        set({ coins: s.coins - item.cost, owned, featuredGoal: nextGoal });
      },
      setGoal: (itemId) =>
        set((s) => (s.owned.includes(itemId) ? {} : { featuredGoal: itemId })),
      recordSpeedRun: (beatId, timeMs, wordCount) =>
        set((s) => {
          const prev = s.fluency.bestMsByBeat[beatId];
          const best = prev === undefined ? timeMs : Math.min(prev, timeMs);
          const recentWpm = [...s.fluency.recentWpm, Math.round(wpm(wordCount, timeMs))].slice(-10);
          return { fluency: { bestMsByBeat: { ...s.fluency.bestMsByBeat, [beatId]: best }, recentWpm } };
        }),
      markIntroSeen: (level) =>
        set((s) =>
          s.storySeen.intros.includes(level)
            ? {}
            : { storySeen: { ...s.storySeen, intros: [...s.storySeen.intros, level] } },
        ),
      markVictorySeen: (level) =>
        set((s) =>
          s.storySeen.victories.includes(level)
            ? {}
            : { storySeen: { ...s.storySeen, victories: [...s.storySeen.victories, level] } },
        ),
      resetProgress: () =>
        set({
          mastery: {},
          session: 0,
          band: DEFAULT_BAND,
          placementDone: false,
          currentBeatIds: [],
          beatIndex: 0,
          owned: [],
          featuredGoal: null,
          fluency: emptyFluency(),
          storySeen: emptyStory(),
        }),
    }),
    {
      name: 'crack-the-code:save',
      version: 5,
      // v1 stored coins + a linear mission index; carry coins, reset to the engine model.
      // v2 added the SR engine. v3 added the reward economy. v4 added fluency. v5 adds the
      // story spine (storySeen). For any engine-era save (v>=2) PRESERVE all progress and
      // only default the newer fields.
      migrate: (persisted, version) => {
        const old = (persisted ?? {}) as Partial<GameState> & { coins?: number; settings?: Settings };
        if (version < 2) {
          // Pre-engine save: carry coins only, start the engine fresh.
          return {
            coins: old.coins ?? 0,
            mastery: {},
            session: 0,
            band: DEFAULT_BAND,
            placementDone: false,
            currentBeatIds: [],
            beatIndex: 0,
            owned: [],
            featuredGoal: null,
            kidName: null,
            fluency: emptyFluency(),
            storySeen: emptyStory(),
            settings: old.settings ?? { hearItHints: true },
          } as unknown as GameState;
        }
        // v2/v3/v4 -> v5: keep everything, just add any missing newer fields.
        return {
          ...old,
          owned: old.owned ?? [],
          featuredGoal: old.featuredGoal ?? null,
          kidName: old.kidName ?? null,
          fluency: old.fluency ?? emptyFluency(),
          storySeen: old.storySeen ?? emptyStory(),
        } as unknown as GameState;
      },
    },
  ),
);
